import type { MotionEditorActor } from '../types';
import type {
  KeyPoseGenerationHistoryEntry,
  KeyPoseGenerationHistoryStatus,
} from './types';

const AVAILABLE_COMPARISON_GHOST_STATUSES =
  new Set<KeyPoseGenerationHistoryStatus>([
    'generated',
    'applied',
    'apply-failed',
  ]);
const SELECTABLE_COMPARISON_GHOST_STATUSES =
  new Set<KeyPoseGenerationHistoryStatus>(['generated', 'apply-failed']);

export interface KeyPoseComparisonGhostScope {
  appliedMotionEditJobID: string | null;
  bvhUrls: readonly string[];
  sceneID: string;
  referenceName: string;
  motionID: string;
  originalBvhURL: string | null;
  timelineIndex: number;
}

export function getKeyPoseComparisonGhostScopeKey(
  scope: KeyPoseComparisonGhostScope | null | undefined,
) {
  if (!scope) {
    return null;
  }

  return [scope.sceneID, scope.referenceName, scope.timelineIndex].join('::');
}

export function isKeyPoseComparisonGhostHistoryEntryPlayable(
  entry: KeyPoseGenerationHistoryEntry,
  scope?: KeyPoseComparisonGhostScope | null,
) {
  return (
    isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope) &&
    !isKeyPoseComparisonGhostHistoryEntryOnStage(entry, scope) &&
    SELECTABLE_COMPARISON_GHOST_STATUSES.has(entry.status)
  );
}

export function selectAppliedKeyPoseComparisonGhostHistoryEntries(
  entries: readonly KeyPoseGenerationHistoryEntry[],
  scope?: KeyPoseComparisonGhostScope | null,
) {
  const stageAppliedEntries = entries.filter((entry) =>
    isKeyPoseComparisonGhostHistoryEntryOnStage(entry, scope),
  );
  if (stageAppliedEntries.length > 0) {
    return stageAppliedEntries;
  }

  const statusAppliedEntries = entries.filter(
    (entry) =>
      isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope) &&
      entry.status === 'applied',
  );
  if (statusAppliedEntries.length > 0) {
    return statusAppliedEntries;
  }

  const baselineEntry = entries.find(
    (entry) =>
      entry.isBaseline &&
      isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope) &&
      isKeyPoseComparisonGhostHistoryEntryOnStage(entry, scope) &&
      AVAILABLE_COMPARISON_GHOST_STATUSES.has(entry.status),
  );

  return baselineEntry ? [baselineEntry] : [];
}

export function selectAvailableKeyPoseComparisonGhostHistoryEntries(
  entries: readonly KeyPoseGenerationHistoryEntry[],
  scope?: KeyPoseComparisonGhostScope | null,
) {
  return entries.filter(
    (entry) =>
      isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope) &&
      AVAILABLE_COMPARISON_GHOST_STATUSES.has(entry.status),
  );
}

export function selectPlayableKeyPoseComparisonGhostHistoryEntries(
  entries: readonly KeyPoseGenerationHistoryEntry[],
  scope?: KeyPoseComparisonGhostScope | null,
) {
  const appliedJobIDs = new Set(
    selectAppliedKeyPoseComparisonGhostHistoryEntries(entries, scope).map(
      (entry) => entry.jobID,
    ),
  );

  return entries.filter(
    (entry) =>
      isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope) &&
      !appliedJobIDs.has(entry.jobID) &&
      SELECTABLE_COMPARISON_GHOST_STATUSES.has(entry.status),
  );
}

export function selectDefaultKeyPoseComparisonGhostHistoryEntry(
  entries: readonly KeyPoseGenerationHistoryEntry[],
  scope?: KeyPoseComparisonGhostScope | null,
) {
  const playableEntries = selectPlayableKeyPoseComparisonGhostHistoryEntries(
    entries,
    scope,
  );
  const availableEntries = selectAvailableKeyPoseComparisonGhostHistoryEntries(
    entries,
    scope,
  );
  if (availableEntries.length <= 1 || playableEntries.length === 0) {
    return null;
  }

  return (
    playableEntries.find((entry) => entry.isBaseline) ??
    playableEntries[playableEntries.length - 1] ??
    null
  );
}

export function selectKeyPoseComparisonGhostHistoryEntry(
  entries: readonly KeyPoseGenerationHistoryEntry[],
  selectedHistoryJobID: string | null | undefined,
  scope?: KeyPoseComparisonGhostScope | null,
) {
  const playableEntries = selectPlayableKeyPoseComparisonGhostHistoryEntries(
    entries,
    scope,
  );
  const availableEntries = selectAvailableKeyPoseComparisonGhostHistoryEntries(
    entries,
    scope,
  );
  if (availableEntries.length <= 1 || playableEntries.length === 0) {
    return null;
  }

  const selectedEntry = selectedHistoryJobID
    ? playableEntries.find((entry) => entry.jobID === selectedHistoryJobID)
    : null;

  return (
    selectedEntry ??
    playableEntries.find((entry) => entry.isBaseline) ??
    playableEntries[playableEntries.length - 1] ??
    null
  );
}

export function createHistoryBvhComparisonGhostUrls(
  actor: MotionEditorActor,
  entry: KeyPoseGenerationHistoryEntry,
) {
  const outputBvhURL = entry.outputBvhURL?.trim();
  if (!outputBvhURL) {
    return [] as string[];
  }

  return actor.timelineMotions.flatMap((motion) => {
    if (motion.timelineIndex === entry.timelineIndex) {
      return [outputBvhURL];
    }

    return motion.bvhUrls;
  });
}

function isKeyPoseComparisonGhostHistoryEntryInScope(
  entry: KeyPoseGenerationHistoryEntry,
  scope?: KeyPoseComparisonGhostScope | null,
) {
  if (!scope) {
    return true;
  }

  return (
    entry.sceneID === scope.sceneID &&
    entry.referenceName === scope.referenceName &&
    entry.timelineIndex === scope.timelineIndex
  );
}

export function isKeyPoseComparisonGhostHistoryEntryOnStage(
  entry: KeyPoseGenerationHistoryEntry,
  scope?: KeyPoseComparisonGhostScope | null,
) {
  if (!scope || !isKeyPoseComparisonGhostHistoryEntryInScope(entry, scope)) {
    return false;
  }

  if (scope.appliedMotionEditJobID) {
    return entry.jobID === scope.appliedMotionEditJobID;
  }

  if (entry.jobID === scope.motionID) {
    return true;
  }

  const outputBvhURL = entry.outputBvhURL;
  if (
    outputBvhURL &&
    scope.bvhUrls.some((bvhURL) => areBvhUrlsEquivalent(outputBvhURL, bvhURL))
  ) {
    return true;
  }

  const originalBvhURL = scope.originalBvhURL;
  return Boolean(
    entry.isBaseline &&
      originalBvhURL &&
      scope.bvhUrls.some((bvhURL) =>
        areBvhUrlsEquivalent(originalBvhURL, bvhURL),
      ),
  );
}

function areBvhUrlsEquivalent(leftUrl: string, rightUrl: string) {
  const leftParts = getBvhUrlComparisonParts(leftUrl);
  const rightParts = getBvhUrlComparisonParts(rightUrl);

  if (!leftParts || !rightParts) {
    return false;
  }

  if (
    leftParts.href === rightParts.href ||
    leftParts.path === rightParts.path
  ) {
    return true;
  }

  return (
    leftParts.path.length > 0 &&
    rightParts.path.length > 0 &&
    (leftParts.path.endsWith(rightParts.path) ||
      rightParts.path.endsWith(leftParts.path))
  );
}

function getBvhUrlComparisonParts(url: string) {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    parsedUrl.search = '';
    parsedUrl.hash = '';
    return {
      href: parsedUrl.toString(),
      path: normalizeBvhUrlPathForComparison(parsedUrl.pathname),
    };
  } catch {
    const [withoutHash] = trimmedUrl.split('#');
    const [withoutQuery] = withoutHash.split('?');
    return {
      href: withoutQuery,
      path: normalizeBvhUrlPathForComparison(withoutQuery),
    };
  }
}

function normalizeBvhUrlPathForComparison(path: string) {
  const decodedPath = safeDecodeURIComponent(path);
  return decodedPath.replace(/^\/+/, '');
}

function safeDecodeURIComponent(value: string) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
